import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  CreditCard, Receipt, Check, Clock, AlertCircle, ChevronRight,
  Star, Users, Zap, ArrowLeft, Download, Calendar, IndianRupee
} from 'lucide-react';
import ParentNav from '../components/ParentNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ParentSubscription = () => {
  const navigate = useNavigate();
  
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [subscribing, setSubscribing] = useState(false);
  const [activeTab, setActiveTab] = useState('subscription');

  const getToken = () => localStorage.getItem('parent_token');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/parent/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = getToken();
      
      const [plansRes, subRes, invRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscription/plans`),
        axios.get(`${BACKEND_URL}/api/parent/subscription?token=${token}`),
        axios.get(`${BACKEND_URL}/api/parent/invoices?token=${token}`)
      ]);
      
      setPlans(plansRes.data.plans || {});
      setSubscription(subRes.data.subscription);
      setInvoices(invRes.data.invoices || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      setSubscribing(true);
      const token = getToken();
      
      const response = await axios.post(
        `${BACKEND_URL}/api/subscription/subscribe`,
        null,
        {
          params: {
            token,
            plan_id: planId,
            billing_period: billingPeriod
          }
        }
      );
      
      if (response.data.success) {
        alert('Subscription created! Please complete payment to activate.');
        loadData();
        setSelectedPlan(null);
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      alert('Failed to create subscription');
    } finally {
      setSubscribing(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      const token = getToken();
      
      // Mock payment - in production, this would open a payment gateway
      const response = await axios.post(
        `${BACKEND_URL}/api/invoice/${invoiceId}/pay`,
        null,
        {
          params: {
            token,
            payment_method: 'card',
            payment_id: `pay_mock_${Date.now()}`
          }
        }
      );
      
      if (response.data.success) {
        alert('Payment successful! Your subscription is now active.');
        loadData();
      }
    } catch (error) {
      console.error('Error paying invoice:', error);
      alert(error.response?.data?.detail || 'Payment failed');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-100', text: 'text-green-700', icon: Check },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      paid: { bg: 'bg-green-100', text: 'text-green-700', icon: Check },
      overdue: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-700', icon: AlertCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {status?.toUpperCase()}
      </span>
    );
  };

  const getPlanIcon = (planId) => {
    const icons = {
      basic: Star,
      standard: Users,
      premium: Zap
    };
    return icons[planId] || Star;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ParentNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50" data-testid="parent-subscription-page">
      <ParentNav />

      <div className="pt-20 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/parent/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-6 h-6 text-orange-500" />
                  Subscription & Billing
                </h1>
                <p className="text-gray-600 text-sm">Manage your subscription and view invoices</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'subscription' ? 'default' : 'outline'}
              onClick={() => setActiveTab('subscription')}
              className={activeTab === 'subscription' ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Subscription
            </Button>
            <Button
              variant={activeTab === 'invoices' ? 'default' : 'outline'}
              onClick={() => setActiveTab('invoices')}
              className={activeTab === 'invoices' ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              <Receipt className="w-4 h-4 mr-2" />
              Invoices
            </Button>
          </div>

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <>
              {/* Current Subscription */}
              {subscription && (
                <Card className="mb-6 border-2 border-orange-200">
                  <CardHeader className="bg-orange-50">
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        Current Subscription
                      </span>
                      {getStatusBadge(subscription.status)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{subscription.plan_name}</h3>
                        <p className="text-gray-500 capitalize">{subscription.billing_period} billing</p>
                        {subscription.end_date && (
                          <p className="text-sm text-gray-400 mt-2">
                            Renews: {new Date(subscription.end_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-orange-600">
                          ₹{subscription.billing_period === 'annually' 
                            ? subscription.plan?.price_annually 
                            : subscription.plan?.price_monthly}
                        </p>
                        <p className="text-sm text-gray-500">
                          /{subscription.billing_period === 'annually' ? 'year' : 'month'}
                        </p>
                      </div>
                    </div>
                    
                    {subscription.plan?.features && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium text-gray-700 mb-2">Your Plan Includes:</p>
                        <div className="grid grid-cols-2 gap-2">
                          {subscription.plan.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                              <Check className="w-4 h-4 text-green-500" />
                              {feature}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Billing Period Toggle */}
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center bg-white rounded-lg p-1 shadow-sm border">
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      billingPeriod === 'monthly'
                        ? 'bg-orange-500 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingPeriod('annually')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      billingPeriod === 'annually'
                        ? 'bg-orange-500 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Annually
                    <span className="ml-1 text-xs text-green-500 bg-green-50 px-1.5 py-0.5 rounded">Save 17%</span>
                  </button>
                </div>
              </div>

              {/* Plans Grid */}
              <div className="grid md:grid-cols-3 gap-6">
                {Object.entries(plans).map(([planId, plan]) => {
                  const PlanIcon = getPlanIcon(planId);
                  const isCurrentPlan = subscription?.plan_id === planId;
                  const price = billingPeriod === 'annually' ? plan.price_annually : plan.price_monthly;
                  
                  return (
                    <Card 
                      key={planId}
                      className={`relative overflow-hidden transition-all ${
                        isCurrentPlan 
                          ? 'border-2 border-orange-500 shadow-lg'
                          : selectedPlan === planId
                            ? 'border-2 border-blue-500 shadow-md'
                            : 'hover:shadow-md'
                      }`}
                    >
                      {isCurrentPlan && (
                        <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs px-3 py-1 rounded-bl-lg">
                          CURRENT
                        </div>
                      )}
                      
                      {planId === 'premium' && !isCurrentPlan && (
                        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xs text-center py-1">
                          MOST POPULAR
                        </div>
                      )}
                      
                      <CardContent className={`p-6 ${planId === 'premium' && !isCurrentPlan ? 'pt-10' : ''}`}>
                        <div className="text-center mb-6">
                          <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${
                            planId === 'basic' ? 'bg-blue-100' :
                            planId === 'standard' ? 'bg-purple-100' : 'bg-yellow-100'
                          }`}>
                            <PlanIcon className={`w-6 h-6 ${
                              planId === 'basic' ? 'text-blue-600' :
                              planId === 'standard' ? 'text-purple-600' : 'text-yellow-600'
                            }`} />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                        </div>
                        
                        <div className="text-center mb-6">
                          <span className="text-4xl font-bold text-gray-900">₹{price}</span>
                          <span className="text-gray-500">/{billingPeriod === 'annually' ? 'year' : 'month'}</span>
                          {billingPeriod === 'annually' && (
                            <p className="text-sm text-orange-600 mt-1">
                              Save ₹{(plan.price_monthly * 12) - plan.price_annually}/year
                            </p>
                          )}
                        </div>
                        
                        <ul className="space-y-3 mb-6">
                          {plan.features?.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        {isCurrentPlan ? (
                          <Button disabled className="w-full bg-gray-100 text-gray-500">
                            Current Plan
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => handleSubscribe(planId)}
                            disabled={subscribing}
                            className={`w-full ${
                              planId === 'premium'
                                ? 'bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600'
                                : 'bg-orange-500 hover:bg-orange-600'
                            }`}
                          >
                            {subscribing ? 'Processing...' : `Choose ${plan.name}`}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-gray-500" />
                  Invoice History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <div className="text-center py-12">
                    <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No invoices yet</p>
                    <p className="text-sm text-gray-400">Your invoices will appear here once you subscribe</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {invoices.map((invoice) => (
                      <div 
                        key={invoice.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            invoice.status === 'paid' ? 'bg-green-100' : 'bg-yellow-100'
                          }`}>
                            {invoice.status === 'paid' 
                              ? <Check className="w-6 h-6 text-green-600" />
                              : <Clock className="w-6 h-6 text-yellow-600" />
                            }
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                            <p className="text-sm text-gray-500">
                              {invoice.plan_name} - {invoice.billing_period}
                            </p>
                            <p className="text-xs text-gray-400">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {new Date(invoice.issue_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold text-lg text-gray-900">
                              <IndianRupee className="w-4 h-4 inline" />
                              {invoice.total_amount}
                            </p>
                            {getStatusBadge(invoice.status)}
                          </div>
                          
                          {invoice.status === 'pending' ? (
                            <Button
                              size="sm"
                              onClick={() => handlePayInvoice(invoice.id)}
                              className="bg-orange-500 hover:bg-orange-600"
                            >
                              Pay Now
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm">
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentSubscription;
